import { feeDivisor, maxFeePercentage } from 'constants/fees'
import { CountryCodeType } from 'data/App/types'
import { defaultLocalCurrency } from 'data/Instrument/types'
import {
  AccumulatorType,
  DeviceInfoType,
  FeeType,
  LimitsType,
  LimitType,
  SessionType,
  StateType,
  SymbolFeeType,
  TierDefinitionType,
  TiersLimitsVolumeType,
  TierType,
  TOtpConfigType,
  UserFeesType,
  UserTiersType,
  UserType,
  WithdrawalPeriodType
} from 'data/types'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import { createSelector } from 'reselect'
import { TIERS } from 'services/api'

import * as extraKYCQuestionsSelectors from './ExtraKYCQuestions/selectors'
import * as localSettingsSelectors from './localSettings/selectors'
import { AuthStateType } from './reducers'
import * as secondFactorChallenge from './SecondFactorChallenge/selectors'
import * as settings from './Settings/selectors'
import * as ukFrictionsSelectors from './UKFrictions/selectors'

const getAuth = (state: StateType): AuthStateType => state.auth || {}

const getApiSettings = createSelector(
  (state: StateType) => getAuth(state).api,
  (api) => api || { keyToDeactivate: '', keys: [], newKey: {} }
)

const getApiKeyToDeactivate = (state: StateType) => getApiSettings(state).keyToDeactivate || ''

const getReferrerUsername = createSelector(getAuth, (auth) => auth.referrerUsername)

const getTotpConfig: (state: StateType) => TOtpConfigType = (state) =>
  getAuth(state).tOtpConfig ?? { backupCodes: [], seed: '' }

const getDeviceInfo: (state: StateType) => void | DeviceInfoType = createSelector(
  getAuth,
  (auth) => auth.deviceInfo
)

const getTwoFactorStatus = (state: StateType) => getAuth(state).twoFactorStatus

const getUser: (state: StateType) => undefined | UserType = createSelector(
  getAuth,
  (auth) => auth.user
)

const isUserLoggedIn: (state: StateType) => boolean = createSelector(
  getUser,
  (user: void | UserType) => !isNil(user)
)

const getUserName: (state: StateType) => string = createSelector(getUser, (user: void | UserType) =>
  user ? [user.firstName, user.lastName].filter((name) => !isNil(name)).join(' ') : ''
)

const getUserTiers: (state: StateType) => void | UserTiersType = createSelector(
  getUser,
  (user: void | UserType) => (user ? user.tiers : user)
)

const getUserFees: (state: StateType) => void | UserFeesType = createSelector(
  getAuth,
  (auth) => auth.fees
)

const getUserId: (state: StateType) => void | string = createSelector(
  getAuth,
  ({ user }) => user?.id
)

const getUserPromos: (state: StateType) => void | UserFeesType = createSelector(
  getAuth,
  (auth) => auth.promos
)

const getUserTiersCurrent: (state: StateType) => undefined | TierType = createSelector(
  getUserTiers,
  (tiers?) => (tiers ? tiers.current : undefined)
)

const SILVERISH_TIERS = [
  TierType.New,
  TierType.Silver,
  TierType.SilverPlus,
  TierType.SilverPlusPlus
]
const isUserSilverishTier = createSelector(
  getUserTiersCurrent,
  (current) => !current || SILVERISH_TIERS.indexOf(current) > -1
)

const getUserTiersSelected: (state: StateType) => void | TierType = createSelector(
  getUserTiers,
  (tiers) => (tiers ? tiers.selected : undefined)
)

const getTiersLimitsVolume: (state: StateType) => void | TiersLimitsVolumeType = createSelector(
  getAuth,
  (auth) => auth.tiersLimitsVolume
)

const getTierDefinitions: (state: StateType) => undefined | TierDefinitionType = createSelector(
  getTiersLimitsVolume,
  (tlf?) => (tlf ? tlf.tiers : undefined)
)

const getLimits: (state: StateType) => void | LimitsType = createSelector(
  getTiersLimitsVolume,
  (tlf) => (tlf ? tlf.limits : undefined)
)

const getLimitPeriod: (state: StateType) => WithdrawalPeriodType = createSelector(
  getUserTiersCurrent,
  (tier = 0) => (tier > TIERS.TIER_ONE ? 'WEEK' : 'YEAR')
)

const getCryptoLimits: (state: StateType) => LimitType = createSelector(
  getLimits,
  getLimitPeriod,
  (limits = {}, period) =>
    limits && limits.WITHDRAWAL_CRYPTO ? limits.WITHDRAWAL_CRYPTO[period] : { limit: 0, used: 0 }
)

const getVolume: (state: StateType) => undefined | number = createSelector(
  getTiersLimitsVolume,
  (tiers?) => (tiers ? tiers.volume : undefined)
)

const getFiatLimits: (state: StateType) => LimitType = createSelector(
  getLimits,
  getLimitPeriod,
  (limits = {}, period) =>
    limits && limits.WITHDRAWAL_FIAT ? limits.WITHDRAWAL_FIAT[period] : { limit: 0, used: 0 }
)

const getAllFees: (state: StateType) => void | SymbolFeeType = createSelector(getUserFees, (fees) =>
  fees ? fees.all : fees
)

const getFees: (state: StateType) => void | FeeType = createSelector(getAllFees, (fees) =>
  fees ? fees.active : undefined
)

const getMaxFee: (state: StateType) => number = createSelector(getFees, (fees) =>
  fees ? Math.max(fees.makerFee, fees.takerFee) / feeDivisor : maxFeePercentage
)

const getEmail: (state: StateType) => undefined | string = createSelector(
  getUser,
  (user: void | UserType) => (user ? user.email : undefined)
)

const getSession: (state: StateType) => void | SessionType = createSelector(
  getAuth,
  (auth) => auth.session
)

const getTags: (state: StateType) => void | any = createSelector(getUser, (user: void | UserType) =>
  user && user.tags ? user.tags : {}
)

const getWalletGuid = (state: StateType) => {
  const user = getUser(state)
  return user?.unifiedAccountWalletGuid ?? user?.walletGuid ?? undefined
}

const isWalletLinked: (state: StateType) => boolean = createSelector(
  getWalletGuid,
  (address) => !isNil(address)
)

const isAuthenticated: (state: StateType) => boolean = createSelector(
  getAuth,
  (auth) => !!(!isNil(auth.user) && auth.user.settings)
)

const isTwoFactorStatusLoading: (state: StateType) => boolean = createSelector(getAuth, (auth) =>
  isNil(auth.twoFactorStatus)
)

const getEmailVerificationId: (state: StateType) => void | string = createSelector(
  getAuth,
  (auth) => auth.emailVerificationId
)

const getCountry: (state: StateType) => void | CountryCodeType = createSelector(getUser, (user) =>
  user && user.address ? user.address.country : undefined
)

const getLocalCurrency: (state: StateType) => string = createSelector(
  getUser,
  (user) => user?.settings?.localCurrency ?? defaultLocalCurrency
)

const getShowNotifications: (state: StateType) => boolean = createSelector(
  getUser,
  (user) => user?.settings?.showNotifications ?? true
)

const makeIsCountry: (country: CountryCodeType) => (state: StateType) => boolean = (country) =>
  createSelector(getCountry, (userCountry) => userCountry === country)

const getAggregatedFees: (state: StateType) => void | Array<AccumulatorType> = createSelector(
  getAuth,
  (auth) => auth.aggregatedFees
)

const getMonthlyAggregatedFees: (state: StateType) => number = createSelector(
  getAggregatedFees,
  (fees) => {
    if (isNil(fees) || isEmpty(fees) || !fees) {
      return 0
    }
    const monthlyFees: void | AccumulatorType = fees.find((fee) => fee.type === 'MONTH')
    return monthlyFees ? Number(monthlyFees.amount.value) : 0
  }
)
const getDailyAggregatedFees: (state: StateType) => number = createSelector(
  getAggregatedFees,
  (fees) => {
    if (isNil(fees) || isEmpty(fees) || !fees) {
      return 0
    }
    const dailyFees: void | AccumulatorType = fees.find((fee) => fee.type === 'DAY')
    return dailyFees ? Number(dailyFees.amount.value) : 0
  }
)

const isHighRisk: (state: StateType) => boolean = createSelector(
  getTags,
  (tags) => tags?.HIGH_RISK_USER?.status === 'PENDING'
)

const isAuthing = (state: StateType) => state.auth.isAuthing

export default {
  extraKYCQuestions: extraKYCQuestionsSelectors,
  getAllFees,
  getApiKeyToDeactivate,
  getApiSettings,
  getAuth,
  getCountry,
  getCryptoLimits,
  getDailyAggregatedFees,
  getDeviceInfo,
  getEmail,
  getEmailVerificationId,
  getFees,
  getFiatLimits,
  getLimitPeriod,
  getLimits,
  getLocalCurrency,
  getMaxFee,
  getMonthlyAggregatedFees,
  getReferrerUsername,
  getSession,
  getShowNotifications,
  getTags,
  getTierDefinitions,
  getTiersLimitsVolume,
  getTotpConfig,
  getTwoFactorStatus,
  getUser,
  getUserFees,
  getUserId,
  getUserName,
  getUserPromos,
  getUserTiers,
  getUserTiersCurrent,
  getUserTiersSelected,
  getVolume,
  getWalletGuid,
  isAuthenticated,
  isAuthing,
  isHighRisk,
  isTwoFactorStatusLoading,
  isUserLoggedIn,
  isUserSilverishTier,
  isWalletLinked,
  localSettings: localSettingsSelectors,
  makeIsCountry,
  secondFactorChallenge,
  settings,
  ukFrictions: ukFrictionsSelectors
}
